import React from "react";

// Helper function to convert relative URLs to absolute URLs
const toAbsoluteUrl = (relativeUrl) => {
  if (!relativeUrl) return "";
  if (relativeUrl.startsWith("http")) return relativeUrl;
  return `${
    process.env.REACT_APP_API_URL || "http://localhost:3001"
  }${relativeUrl}`;
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Helper function to get document type label
const getDocumentTypeLabel = (documentType) => {
  const labels = {
    experience_documents: "Experience Documents",
    pan_card_image: "PAN Card Image",
    kyc_document: "KYC Document",
    kyc_image: "KYC Image",
    cv_upload: "CV Upload",
    photo_upload: "Photo Upload",
  };
  return labels[documentType] || documentType;
};

// Individual Document Card Component
const DocumentCard = ({ document, documentType }) => {
  if (!document) return null;

  const isImage = (document.mimeType || "").startsWith("image/");
  const isPdf = (document.mimeType || "") === "application/pdf";

  return (
    <div className="border rounded-lg p-3 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {/* Preview/Icon */}
        <div className="flex-shrink-0">
          {isImage ? (
            <img
              src={toAbsoluteUrl(document.url)}
              alt={document.originalName || "Document"}
              className="w-16 h-16 object-cover rounded border"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : isPdf ? (
            <div className="w-16 h-16 bg-red-100 rounded border flex items-center justify-center">
              <span className="text-red-600 font-bold text-xs">PDF</span>
            </div>
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
              <span className="text-gray-600 font-bold text-xs">DOC</span>
            </div>
          )}
          {/* Fallback for broken images */}
          {isImage && (
            <div className="w-16 h-16 bg-gray-100 rounded border items-center justify-center hidden">
              <span className="text-gray-600 font-bold text-xs">IMG</span>
            </div>
          )}
        </div>

        {/* Document Info */}
        <div className="flex-grow min-w-0">
          <h4 className="font-medium text-gray-900 truncate">
            {getDocumentTypeLabel(documentType)}
          </h4>
          <p className="text-sm text-gray-600 truncate">
            {document.originalName || "Unknown file"}
          </p>
          <p className="text-xs text-gray-500">
            {formatFileSize(document.size)} • {document.mimeType}
          </p>
          <p className="text-xs text-gray-400">
            Uploaded: {new Date(document.uploadedAt).toLocaleDateString()}
          </p>

          {/* Verification Status */}
          {document.isVerified !== undefined && (
            <div className="mt-1">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  document.isVerified
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {document.isVerified ? "✓ Verified" : "⏳ Pending"}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          <button
            onClick={() => window.open(toAbsoluteUrl(document.url), "_blank")}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Document Viewer Component
const DocumentViewer = ({ documents, title = "Documents" }) => {
  if (!documents || typeof documents !== "object") {
    return (
      <div className="bg-white p-4 rounded-lg border">
        <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-500 text-sm">No documents available</p>
      </div>
    );
  }

  const documentEntries = Object.entries(documents).filter(([_, doc]) => doc);

  if (documentEntries.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg border">
        <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-500 text-sm">No documents uploaded</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg border">
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {documentEntries.map(([documentType, document]) => (
          <DocumentCard
            key={documentType}
            document={document}
            documentType={documentType}
          />
        ))}
      </div>
    </div>
  );
};

// Compact Document List (for tables/lists)
const DocumentList = ({ documents, maxDisplay = 3 }) => {
  if (!documents || typeof documents !== "object")
    return <span className="text-gray-500">No docs</span>;

  const documentEntries = Object.entries(documents).filter(([_, doc]) => doc);

  if (documentEntries.length === 0) {
    return <span className="text-gray-500">No docs</span>;
  }

  const displayDocs = documentEntries.slice(0, maxDisplay);
  const remainingCount = documentEntries.length - maxDisplay;

  return (
    <div className="flex flex-wrap gap-1">
      {displayDocs.map(([documentType, document]) => (
        <button
          key={documentType}
          onClick={() => window.open(toAbsoluteUrl(document.url), "_blank")}
          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 transition-colors"
          title={`${getDocumentTypeLabel(documentType)}: ${
            document.originalName
          }`}
        >
          <span className="truncate max-w-20">
            {getDocumentTypeLabel(documentType)}
          </span>
        </button>
      ))}
      {remainingCount > 0 && (
        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

export { DocumentViewer, DocumentList, DocumentCard };
